import { Controller } from "@hotwired/stimulus"

// Phase 2.11 Tier 1 — Asset form helper.
//
// Drives the inline-create modals for "+ New System" / "+ New Location"
// so PMs filling the asset form can add a system or building/floor
// without leaving the form. The modals POST to AssetSystemsController#create
// / AssetLocationsController#create with `Accept: application/json`,
// receive the created row, and append a freshly-selected option to the
// associated select.
//
// Routes are passed via Stimulus values so the controller is project-
// agnostic (the form template knows the project; the controller is
// reusable across projects).
export default class extends Controller {
  static targets = [
    "systemSelect", "locationSelect",
    "systemModal", "systemName", "systemKind", "systemError",
    "locationModal", "locationBuilding", "locationFloor", "locationError",
    // Phase 2.11 Tier 5 — Equipment Library picker.
    "equipmentLibraryModal", "libraryFilter", "libraryRow", "libraryEmpty",
    "equipmentStandardId", "equipmentStandardSnapshot"
  ]
  static values = { systemsPath: String, locationsPath: String }

  // ─── System inline-create ─────────────────────────────────────────

  openSystemModal() {
    this._showModal(this.systemModalTarget)
    this._clearError(this.systemErrorTarget)
    this.systemNameTarget.value = ""
    this.systemKindTarget.value = ""
    setTimeout(() => this.systemNameTarget.focus(), 30)
  }

  closeSystemModal(event) {
    if (event && event.target !== event.currentTarget) return
    this._hideModal(this.systemModalTarget)
  }

  async submitSystem() {
    const name = this.systemNameTarget.value.trim()
    if (!name) {
      this._setError(this.systemErrorTarget, "Name is required.")
      return
    }

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const res = await fetch(this.systemsPathValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({
          asset_system: { name, system_kind: this.systemKindTarget.value }
        }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._setError(this.systemErrorTarget,
          (data.errors && data.errors.join(" · ")) || "Failed to add system.")
        return
      }
      const opt = document.createElement("option")
      opt.value = data.system.id
      opt.textContent = data.system.label
      opt.selected = true
      this.systemSelectTarget.appendChild(opt)
      this._hideModal(this.systemModalTarget)
    } catch (err) {
      console.error("[asset-form] system create failed", err)
      this._setError(this.systemErrorTarget, "Network error. Try again.")
    }
  }

  // ─── Location inline-create ───────────────────────────────────────

  openLocationModal() {
    this._showModal(this.locationModalTarget)
    this._clearError(this.locationErrorTarget)
    this.locationBuildingTarget.value = ""
    this.locationFloorTarget.value = ""
    setTimeout(() => this.locationBuildingTarget.focus(), 30)
  }

  closeLocationModal(event) {
    if (event && event.target !== event.currentTarget) return
    this._hideModal(this.locationModalTarget)
  }

  async submitLocation() {
    const building = this.locationBuildingTarget.value.trim()
    if (!building) {
      this._setError(this.locationErrorTarget, "Building is required.")
      return
    }

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const res = await fetch(this.locationsPathValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf || ""
        },
        body: JSON.stringify({
          asset_location: {
            building_label: building,
            floor_label: this.locationFloorTarget.value.trim()
          }
        }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._setError(this.locationErrorTarget,
          (data.errors && data.errors.join(" · ")) || "Failed to add location.")
        return
      }
      const opt = document.createElement("option")
      opt.value = data.location.id
      opt.textContent = data.location.label
      opt.selected = true
      this.locationSelectTarget.appendChild(opt)
      this._hideModal(this.locationModalTarget)
    } catch (err) {
      console.error("[asset-form] location create failed", err)
      this._setError(this.locationErrorTarget, "Network error. Try again.")
    }
  }

  // Stops backdrop click handlers from closing the modal when the user
  // is interacting with the panel itself.
  stopPropagation(event) {
    event.stopPropagation()
  }

  // ─── Phase 2.11 Tier 5 — Equipment Library picker ─────────────────

  openEquipmentLibraryModal() {
    if (!this.hasEquipmentLibraryModalTarget) return
    this._showModal(this.equipmentLibraryModalTarget)
    if (this.hasLibraryFilterTarget) {
      this.libraryFilterTarget.value = ""
      this.libraryRowTargets.forEach((r) => r.classList.remove("hidden"))
      if (this.hasLibraryEmptyTarget) this.libraryEmptyTarget.classList.add("hidden")
      setTimeout(() => this.libraryFilterTarget.focus(), 30)
    }
  }

  closeEquipmentLibraryModal(event) {
    if (event && event.target !== event.currentTarget) return
    if (this.hasEquipmentLibraryModalTarget) {
      this._hideModal(this.equipmentLibraryModalTarget)
    }
  }

  filterEquipmentLibrary() {
    const q = (this.libraryFilterTarget.value || "").toLowerCase().trim()
    let visible = 0
    this.libraryRowTargets.forEach((row) => {
      const haystack = row.dataset.search || ""
      const match = q === "" || haystack.includes(q)
      row.classList.toggle("hidden", !match)
      if (match) visible++
    })
    if (this.hasLibraryEmptyTarget) {
      this.libraryEmptyTarget.classList.toggle("hidden", visible > 0)
    }
  }

  selectEquipmentStandard(event) {
    const row = event.currentTarget
    const raw = row.dataset.prefill
    if (!raw) return

    let prefill
    try {
      prefill = JSON.parse(raw)
    } catch (err) {
      console.error("[asset-form] could not parse prefill", err)
      return
    }

    // Walk the form's named inputs and fill in only the empty ones —
    // operators who already typed values keep them. The standard's id +
    // snapshot are the exception: those always overwrite so the
    // copy-down attribution stays accurate.
    Object.entries(prefill).forEach(([key, value]) => {
      if (value == null || value === "") return
      const isAttribution = key === "originating_equipment_standard_id" ||
                            key === "equipment_standard_snapshot_json"
      const input = this.element.querySelector(`[name="asset[${key}]"]`)
      if (!input) return

      if (isAttribution) {
        input.value = key === "equipment_standard_snapshot_json"
          ? (typeof value === "string" ? value : JSON.stringify(value))
          : value
        return
      }

      const empty = (input.value == null || input.value.trim() === "")
      if (!empty) return

      if (input.tagName === "SELECT") {
        const opt = Array.from(input.options).find((o) => o.value === String(value))
        if (opt) input.value = String(value)
      } else {
        input.value = value
      }
      input.dispatchEvent(new Event("change", { bubbles: true }))
    })

    this.closeEquipmentLibraryModal()
  }

  clearEquipmentStandard() {
    if (this.hasEquipmentStandardIdTarget) this.equipmentStandardIdTarget.value = ""
    if (this.hasEquipmentStandardSnapshotTarget) this.equipmentStandardSnapshotTarget.value = ""
    // Force re-render — the green badge reads from the form value at
    // ERB render time, but a fresh client-side detach + submit is the
    // cleanest path. Hide the badge instead of reloading.
    const badge = this.element.querySelector(".bg-emerald-50.border-emerald-200")
    if (badge) badge.classList.add("hidden")
  }

  // ─── Helpers ───────────────────────────────────────────────────

  _showModal(modal) {
    modal.classList.remove("hidden")
  }

  _hideModal(modal) {
    modal.classList.add("hidden")
  }

  _setError(target, message) {
    target.textContent = message
    target.classList.remove("hidden")
  }

  _clearError(target) {
    target.textContent = ""
    target.classList.add("hidden")
  }
}
